import { Link, types, useAdminContext, usePageValues } from "react-bricks/frontend"
import React, { useEffect, useState } from "react"
import { getCookie, setCookie } from "../../shared/utils/cookie"

import { Container } from "../components/layout"
import { Icon } from "../components/icon"
import { Icons } from "../../types"
import { LazyImage } from "../../shared/components/universal/image"
import { formatPrice } from "../../shared/utils/format"
import { useLocalesState } from "../../shared/context/locales"
import { usePageContext } from "../../context/page"
import { useQuery } from "@tanstack/react-query"
import { useSDK } from "../../shared/context/client"
import { useTranslation } from "react-i18next"

const SKU = "SSB"

const Button = () => {
    const { t } = useTranslation("translations")

    return (
        <Link
            className="flex h-14 w-full items-center justify-center rounded-sm border border-solid border-white px-5 text-base text-white sm:h-auto sm:w-auto sm:px-7 sm:py-3"
            href={`${process.env.SHOP_URL}/cart?add_products=[{"sku":"${SKU}","quantity":1}]`}
        >
            <Icon icon={Icons.cart} className="mr-2.5 lg:mr-3" />
            <span className="pt-1">{t("uk_teaser_button")}</span>
        </Link>
    )
}

const UKTeaser: types.Brick = () => {
    const { t } = useTranslation("translations")
    const { region, language } = useLocalesState()

    const { isAdmin } = useAdminContext()

    const [closed, setClosed] = useState(false)

    useEffect(() => {
        setClosed(getCookie("uk-teaser-closed") === "true")
    }, [])

    const close = () => {
        setClosed(true)
        setCookie("uk-teaser-closed", "true")
    }

    const { products: productsBuildTime, page: pageContext } = usePageContext()

    const client = useSDK()

    const [pageValues] = usePageValues()

    const page = pageContext || pageValues

    const { data } = useQuery({
        queryKey: ["products", SKU, region, page.language],
        queryFn: ({ signal }) => client.getProducts({ sku: SKU, country: region }, page.language, signal),
        meta: { background: true },
    })

    const products = data || productsBuildTime
    const SSB = products?.find(product => product.sku === SKU)

    if (!SSB && !isAdmin) return null
    if (closed) return null

    return (
        <div className="bg-darkblumine text-white">
            <Container>
                <div className="gap-7 py-7 sm:flex lg:items-center lg:py-5">
                    <div className="ml-[15px] hidden w-[118px] shrink-0 sm:block md:ml-[34px] lg:ml-[67px] lg:w-[137px] xl:ml-[114px]">
                        <LazyImage
                            url="assets"
                            path="/lavita.de/uk/website_banner_UK_LaVita-and-travel-bottle_V2.png"
                            srcSet={[{ w: 118 }, { w: 137, default: true }, { w: 236 }, { w: 274 }]}
                            sizes="(min-width: 1366px) 137px, 118px"
                        />
                    </div>
                    <div>
                        <div className="flex items-center gap-7">
                            <div className="items-center gap-10 lg:flex xl:gap-16">
                                <div className="flex justify-between">
                                    <div>
                                        <h2 className="sm:text-l font-gibson text-xl font-semibold lg:text-xl">
                                            {t("uk_teaser_headline")}
                                        </h2>
                                        <p className="mt-2 text-sm lg:text-base">{t("uk_teaser_description")}</p>
                                    </div>
                                    <div className="sm:hidden">
                                        <button
                                            type="button"
                                            onClick={close}
                                            className="lavita-icon block text-base leading-none"
                                        >
                                            
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <div className="mt-2 items-center gap-7 sm:mt-7 sm:flex md:mt-5 lg:mt-0 lg:gap-10">
                                        <div className="flex items-center justify-between">
                                            <div className="flex flex-col gap-x-3 gap-y-2 md:flex-row lg:flex-col">
                                                <div className="flex flex-wrap items-center gap-x-3 sm:flex-nowrap">
                                                    <span className="font-gibson text-md font-semibold lg:text-lg">
                                                        {SSB &&
                                                            t("uk_teaser_price", {
                                                                price: formatPrice(
                                                                    SSB.price.current.taxed,
                                                                    SSB.price.currency.name,
                                                                    language
                                                                ),
                                                            })}
                                                    </span>
                                                    <small className="text-xs">
                                                        {SSB &&
                                                            t("uk_teaser_price_incl_vat", {
                                                                price: formatPrice(
                                                                    SSB.price.basic.taxed,
                                                                    SSB.price.currency.name,
                                                                    language
                                                                ),
                                                            })}
                                                    </small>
                                                </div>
                                                <span className="rounded-full bg-orange px-3 pb-1 pt-1.5 text-center text-xs font-semibold leading-none text-white lg:text-sm lg:leading-none">
                                                    {t("uk_teaser_badge")}
                                                </span>
                                            </div>
                                            <div className="w-[116px] sm:hidden">
                                                <LazyImage
                                                    url="assets"
                                                    path="/lavita.de/uk/website_banner_UK_LaVita-and-travel-bottle_V2.png"
                                                    srcSet={[{ w: 116, default: true }, { w: 232 }]}
                                                    sizes="116px"
                                                />
                                            </div>
                                        </div>
                                        <div className="mt-3.5 sm:mt-0 md:hidden lg:block">
                                            <Button />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="hidden md:block lg:hidden">
                                <Button />
                            </div>
                        </div>
                    </div>
                    <div className="hidden grow items-start justify-end sm:flex md:items-center">
                        <button
                            type="button"
                            onClick={close}
                            className="lavita-icon block text-base leading-none lg:text-md"
                        >
                            
                        </button>
                    </div>
                </div>
            </Container>
        </div>
    )
}

UKTeaser.schema = {
    name: "UKTeaser",
    label: "UK Teaser",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/UKTeaser.png`,
}

export default UKTeaser
